import { useState } from 'react'
import { useAlerts, useResolveAlert } from '../api/hooks'
import type { AlertEvent } from '../api/client'
import AutomationPanel from '../components/AutomationPanel'

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high:     'bg-orange-100 text-orange-800 border-orange-200',
  medium:   'bg-yellow-100 text-yellow-800 border-yellow-200',
  warning:  'bg-yellow-100 text-yellow-800 border-yellow-200',
  low:      'bg-blue-100 text-blue-800 border-blue-200',
  info:     'bg-gray-100 text-gray-600 border-gray-200',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function AlertRow({ evt, onResolve, resolving }: {
  evt: AlertEvent
  onResolve: (id: number) => void
  resolving: boolean
}) {
  const severityStyle = SEVERITY_STYLES[evt.severity] ?? SEVERITY_STYLES.info
  const resolved = evt.resolved_at != null
  const hasAutomation = Boolean(evt.forgeops_job_id)
  const [showAuto, setShowAuto] = useState(false)

  return (
    <div className={`bg-white border rounded-xl px-5 py-4 shadow-sm transition-opacity ${resolved ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${severityStyle}`}>
              {evt.severity}
            </span>
            {resolved && (
              <span className="text-xs text-green-600 font-medium">✓ resolved</span>
            )}
          </div>
          <p className="text-sm text-gray-900">{evt.message}</p>
          {evt.device_id && (
            <p className="text-xs text-gray-400 mt-0.5">device #{evt.device_id}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <p className="text-xs text-gray-400">{formatDate(evt.triggered_at)}</p>
          {resolved ? (
            <p className="text-xs text-gray-300">→ {formatDate(evt.resolved_at!)}</p>
          ) : (
            <button
              onClick={() => onResolve(evt.id)}
              disabled={resolving}
              className="text-xs px-2.5 py-1 rounded-md border border-gray-200 text-gray-500
                         hover:border-green-300 hover:text-green-700 hover:bg-green-50
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {resolving ? 'Resolving…' : 'Resolve'}
            </button>
          )}
        </div>
      </div>

      {hasAutomation && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => setShowAuto((v) => !v)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {showAuto ? '▾ Hide automation' : '⚙ View ForgeOps automation'}
          </button>
          {showAuto && <AutomationPanel alertId={evt.id} />}
        </div>
      )}
    </div>
  )
}

export default function AlertsPage() {
  const { data, isLoading, isError, error } = useAlerts()
  const resolve = useResolveAlert()

  const active   = data?.alerts.filter((a: AlertEvent) => !a.resolved_at) ?? []
  const resolved = data?.alerts.filter((a: AlertEvent) =>  a.resolved_at) ?? []

  function handleResolve(id: number) {
    resolve.mutate(id)
  }

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Alerts</h1>
          <p className="text-sm text-gray-400 mt-0.5">Last 50 events, newest first</p>
        </div>
        {data && (
          <span className={`text-sm font-medium ${active.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {active.length > 0 ? `${active.length} active` : 'All clear'}
          </span>
        )}
      </div>

      {resolve.isError && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {resolve.error instanceof Error ? resolve.error.message : 'Failed to resolve alert'}
        </div>
      )}

      {isLoading && (
        <div className="text-sm text-gray-400 py-12 text-center">Loading alerts…</div>
      )}

      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {error instanceof Error ? error.message : 'Failed to load alerts'}
        </div>
      )}

      {data && data.alerts.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">🔔</div>
          <p className="text-sm font-medium">No alerts</p>
          <p className="text-xs mt-1">Alerts appear when a device goes offline or triggers a rule.</p>
        </div>
      )}

      {active.length > 0 && (
        <section className="mb-8">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Active · {active.length}
          </h2>
          <div className="space-y-2">
            {active.map((evt: AlertEvent) => (
              <AlertRow
                key={evt.id}
                evt={evt}
                onResolve={handleResolve}
                resolving={resolve.isPending && resolve.variables === evt.id}
              />
            ))}
          </div>
        </section>
      )}

      {resolved.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Resolved · {resolved.length}
          </h2>
          <div className="space-y-2">
            {resolved.map((evt: AlertEvent) => (
              <AlertRow
                key={evt.id}
                evt={evt}
                onResolve={handleResolve}
                resolving={false}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
